import * as React from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PHONE_COUNTRIES,
  type PhoneCountryCode,
  validateAndNormalizeByCountry,
  stripPhone,
} from "@/lib/phone";

// Nombre max de chiffres saisis localement (hors espaces/séparateurs),
// selon que l'utilisateur tape ou non le 0 initial.
const MAX_LOCAL_DIGITS: Record<PhoneCountryCode, { withLeadingZero: number; withoutLeadingZero: number }> = {
  FR: { withLeadingZero: 10, withoutLeadingZero: 9 }, // 06XXXXXXXX ou 6XXXXXXXX
  BE: { withLeadingZero: 10, withoutLeadingZero: 9 },
  CH: { withLeadingZero: 10, withoutLeadingZero: 9 },
  LU: { withLeadingZero: 0,  withoutLeadingZero: 9 }, // pas de 0 initial
};

function clampLocalInput(country: PhoneCountryCode, raw: string): string {
  const digits = stripPhone(raw).replace(/\D/g, "");
  const limits = MAX_LOCAL_DIGITS[country];
  const max = digits.startsWith("0") ? limits.withLeadingZero : limits.withoutLeadingZero;
  if (max === 0) {
    return digits.replace(/^0+/, "").slice(0, limits.withoutLeadingZero);
  }
  if (digits.length <= max) return raw;
  let kept = 0;
  let out = "";
  for (const ch of raw) {
    if (/\d/.test(ch)) {
      if (kept >= max) continue;
      kept++;
    }
    out += ch;
  }
  return out;
}

interface PhoneCountryInputProps {
  country: PhoneCountryCode;
  onCountryChange: (c: PhoneCountryCode) => void;
  localValue: string;
  onLocalChange: (local: string, normalized: string | null) => void;
  disabled?: boolean;
  id?: string;
  invalid?: boolean;
  className?: string;
  inputClassName?: string;
}

/**
 * Composite phone input: country selector + local-number input.
 * Calls `onLocalChange(local, normalized)` on each keystroke;
 * `normalized` is the stored canonical form (null if invalid).
 */
export function PhoneCountryInput({
  country,
  onCountryChange,
  localValue,
  onLocalChange,
  disabled,
  id,
  invalid,
  className,
  inputClassName,
}: PhoneCountryInputProps) {
  const current = PHONE_COUNTRIES.find((c) => c.code === country) ?? PHONE_COUNTRIES[0];

  return (
    <div className={`flex gap-2 ${className ?? ""}`}>
      <Select
        value={country}
        onValueChange={(v) => {
          const next = v as PhoneCountryCode;
          onCountryChange(next);
          // Re-evaluate normalization for the new country.
          onLocalChange(localValue, validateAndNormalizeByCountry(next, localValue));
        }}
        disabled={disabled}
      >
        <SelectTrigger
          className={`h-12 w-[140px] shrink-0 ${invalid ? "border-destructive" : ""}`}
          aria-label="Indicatif pays"
        >
          <SelectValue>
            <span className="flex items-center gap-2">
              <span aria-hidden>{current.flag}</span>
              <span className="text-sm">{current.dial}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {PHONE_COUNTRIES.map((c) => (
            <SelectItem key={c.code} value={c.code}>
              <span className="flex items-center gap-2">
                <span aria-hidden>{c.flag}</span>
                <span>{c.label}</span>
                <span className="text-muted-foreground">{c.dial}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        placeholder={current.placeholder}
        value={localValue}
        disabled={disabled}
        onChange={(e) => {
          const v = clampLocalInput(country, e.target.value);
          onLocalChange(v, validateAndNormalizeByCountry(country, v));
        }}
        className={`h-12 flex-1 ${invalid ? "border-destructive" : ""} ${inputClassName ?? ""}`}
      />
    </div>
  );
}
